import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const TriangleCalculatorPage = () => {
  const [sideA, setSideA] = useState('');
  const [sideB, setSideB] = useState('');
  const [sideC, setSideC] = useState('');
  const [angleA, setAngleA] = useState('');
  const [angleB, setAngleB] = useState('');
  const [angleC, setAngleC] = useState('');
  const [result, setResult] = useState(null);

  const toRadians = (deg) => deg * (Math.PI / 180);
  const toDegrees = (rad) => rad * (180 / Math.PI);

  const calculate = () => {
    let a = parseFloat(sideA) || 0;
    let b = parseFloat(sideB) || 0;
    let c = parseFloat(sideC) || 0;
    let A = parseFloat(angleA) || 0;
    let B = parseFloat(angleB) || 0;
    let C = parseFloat(angleC) || 0;

    // Count known values
    const knownSides = [a, b, c].filter(x => x > 0).length;
    const knownAngles = [A, B, C].filter(x => x > 0).length;

    // If three angles, ensure they sum to 180
    if (knownAngles === 2) {
      if (A === 0) A = 180 - B - C;
      else if (B === 0) B = 180 - A - C;
      else if (C === 0) C = 180 - A - B;
    }

    // Three sides (SSS)
    if (knownSides === 3 && knownAngles === 0) {
      A = toDegrees(Math.acos((b*b + c*c - a*a) / (2*b*c)));
      B = toDegrees(Math.acos((a*a + c*c - b*b) / (2*a*c)));
      C = 180 - A - B;
    }
    // Two sides and included angle (SAS)
    else if (knownSides === 2 && knownAngles === 1) {
      if (a > 0 && b > 0 && C > 0) {
        c = Math.sqrt(a*a + b*b - 2*a*b*Math.cos(toRadians(C)));
        A = toDegrees(Math.asin(a * Math.sin(toRadians(C)) / c));
        B = 180 - A - C;
      } else if (a > 0 && c > 0 && B > 0) {
        b = Math.sqrt(a*a + c*c - 2*a*c*Math.cos(toRadians(B)));
        A = toDegrees(Math.asin(a * Math.sin(toRadians(B)) / b));
        C = 180 - A - B;
      } else if (b > 0 && c > 0 && A > 0) {
        a = Math.sqrt(b*b + c*c - 2*b*c*Math.cos(toRadians(A)));
        B = toDegrees(Math.asin(b * Math.sin(toRadians(A)) / a));
        C = 180 - A - B;
      }
    }
    // One side and two angles (AAS/ASA)
    else if (knownSides === 1 && knownAngles >= 2) {
      if (A === 0) A = 180 - B - C;
      else if (B === 0) B = 180 - A - C;
      else if (C === 0) C = 180 - A - B;
      
      if (a > 0) {
        b = a * Math.sin(toRadians(B)) / Math.sin(toRadians(A));
        c = a * Math.sin(toRadians(C)) / Math.sin(toRadians(A));
      } else if (b > 0) {
        a = b * Math.sin(toRadians(A)) / Math.sin(toRadians(B));
        c = b * Math.sin(toRadians(C)) / Math.sin(toRadians(B));
      } else if (c > 0) {
        a = c * Math.sin(toRadians(A)) / Math.sin(toRadians(C));
        b = c * Math.sin(toRadians(B)) / Math.sin(toRadians(C));
      }
    }

    // Validate
    if (a <= 0 || b <= 0 || c <= 0 || A <= 0 || B <= 0 || C <= 0 || Math.abs(A + B + C - 180) > 0.01) {
      setResult({ error: 'Invalid triangle. Please provide valid values.' });
      return;
    }

    // Calculate properties
    const perimeter = a + b + c;
    const s = perimeter / 2; // semi-perimeter
    const area = Math.sqrt(s * (s - a) * (s - b) * (s - c)); // Heron's formula
    const inradius = area / s;
    const circumradius = (a * b * c) / (4 * area);

    // Determine triangle type
    let typeByAngles = A === 90 || B === 90 || C === 90 ? 'Right' :
                       A > 90 || B > 90 || C > 90 ? 'Obtuse' : 'Acute';
    let typeBySides = (Math.abs(a - b) < 0.01 && Math.abs(b - c) < 0.01) ? 'Equilateral' :
                      (Math.abs(a - b) < 0.01 || Math.abs(b - c) < 0.01 || Math.abs(a - c) < 0.01) ? 'Isosceles' : 'Scalene';

    setResult({
      sideA: a.toFixed(4), sideB: b.toFixed(4), sideC: c.toFixed(4),
      angleA: A.toFixed(2), angleB: B.toFixed(2), angleC: C.toFixed(2),
      perimeter: perimeter.toFixed(4), area: area.toFixed(4),
      inradius: inradius.toFixed(4), circumradius: circumradius.toFixed(4),
      typeByAngles, typeBySides
    });
  };

  const clear = () => {
    setSideA(''); setSideB(''); setSideC('');
    setAngleA(''); setAngleB(''); setAngleC('');
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <Link to="/math" className="text-cyan-400 hover:text-cyan-300">Math</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Triangle Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Triangle Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate sides, angles, area, and perimeter of a triangle. Enter at least 3 values.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Triangle Diagram */}
              <div className="flex-shrink-0">
                <svg viewBox="0 0 200 180" className="w-48 h-44 mx-auto">
                  <polygon points="100,20 20,160 180,160" fill="none" stroke="#06b6d4" strokeWidth="2" />
                  <text x="100" y="12" fill="#fff" textAnchor="middle" fontSize="12">A</text>
                  <text x="10" y="175" fill="#fff" textAnchor="middle" fontSize="12">B</text>
                  <text x="190" y="175" fill="#fff" textAnchor="middle" fontSize="12">C</text>
                  <text x="50" y="100" fill="#f59e0b" textAnchor="middle" fontSize="11">c</text>
                  <text x="150" y="100" fill="#f59e0b" textAnchor="middle" fontSize="11">b</text>
                  <text x="100" y="175" fill="#f59e0b" textAnchor="middle" fontSize="11">a</text>
                </svg>
              </div>

              <div className="flex-1 grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className="text-white font-semibold">Sides</h3>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-300 w-8">a =</label>
                    <Input type="number" value={sideA} onChange={(e) => setSideA(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-300 w-8">b =</label>
                    <Input type="number" value={sideB} onChange={(e) => setSideB(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-300 w-8">c =</label>
                    <Input type="number" value={sideC} onChange={(e) => setSideC(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="" />
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-white font-semibold">Angles (°)</h3>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-300 w-8">A =</label>
                    <Input type="number" value={angleA} onChange={(e) => setAngleA(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-300 w-8">B =</label>
                    <Input type="number" value={angleB} onChange={(e) => setAngleB(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="" />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-300 w-8">C =</label>
                    <Input type="number" value={angleC} onChange={(e) => setAngleC(e.target.value)}
                      className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={calculate} className="bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>
              <Button onClick={clear} variant="outline" className="border-[#2a2a2a] text-gray-300 hover:bg-[#2a2a2a]">Clear</Button>
            </div>

            {result && !result.error && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-white font-semibold mb-3">Sides & Angles</h4>
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-300">Side a = <span className="text-cyan-400 font-mono">{result.sideA}</span></p>
                      <p className="text-gray-300">Side b = <span className="text-cyan-400 font-mono">{result.sideB}</span></p>
                      <p className="text-gray-300">Side c = <span className="text-cyan-400 font-mono">{result.sideC}</span></p>
                      <p className="text-gray-300">Angle A = <span className="text-amber-400 font-mono">{result.angleA}°</span></p>
                      <p className="text-gray-300">Angle B = <span className="text-amber-400 font-mono">{result.angleB}°</span></p>
                      <p className="text-gray-300">Angle C = <span className="text-amber-400 font-mono">{result.angleC}°</span></p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-3">Properties</h4>
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-300">Area = <span className="text-green-400 font-mono">{result.area}</span></p>
                      <p className="text-gray-300">Perimeter = <span className="text-green-400 font-mono">{result.perimeter}</span></p>
                      <p className="text-gray-300">Inradius = <span className="text-white font-mono">{result.inradius}</span></p>
                      <p className="text-gray-300">Circumradius = <span className="text-white font-mono">{result.circumradius}</span></p>
                      <p className="text-gray-300">Type: <span className="text-purple-400">{result.typeBySides} {result.typeByAngles}</span></p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {result && result.error && (
              <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400">{result.error}</p>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Math Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Scientific', path: '/scientific-calculator' }, { name: 'Percentage', path: '/percentage-calculator' }, { name: 'Fraction', path: '/fraction-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TriangleCalculatorPage;
